import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';
import { PHASES_RETAINED_READ_TIMEOUT_MS } from './mackPhasesEvidence';

export const PHASES_RECOVERY_SCOPE = 'INTERRUPTED_HISTORY_FOUR' as const;
export const PHASES_RECOVERY_CAPABILITY = 'PHASES_INTERRUPTED_HISTORY_CONTINUATION' as const;
export interface PhasesRecoveryAccess {
  authenticated: boolean;
  owner_authorized: boolean;
  session_scope: string;
  session_class: 'OWNER' | 'AUTHENTICATED_NON_OWNER' | 'UNAUTHENTICATED';
  capability: typeof PHASES_RECOVERY_CAPABILITY;
}
export interface PhasesRecoveryClaim {
  contract_id: string;
  claim_identity: string;
  request_id: string;
  callback_identity: string;
  label: string;
  state: string;
  route_state?: string | null;
  source_requests: number;
  rows_added: number;
  persistence_counts?: { returned?: number; admitted?: number; deduplicated?: number; revised?: number; upstream_reused?: number } | null;
  counts_evidence?: string | null;
  response_references?: { url?: string | null; http_status?: number | null; error?: string | null; retrieved_at?: string | null; response_sha256?: string | null }[];
  persistence_complete?: boolean | null;
  persistence_intent_state?: string | null;
  original_source_outcome?: string;
  continuation_response_state?: string;
  reason?: string | null;
}
export interface PhasesRecoveryOperation {
  request_id: string;
  state: string;
  requested_at: string;
  started_at?: string | null;
  ended_at?: string | null;
  claims: PhasesRecoveryClaim[];
}
export interface PhasesRecoveryStatus {
  success: true;
  schema: 'mack_phases_history_recovery_v1';
  scope: typeof PHASES_RECOVERY_SCOPE;
  authority_state: string;
  resident_available: boolean;
  can_submit: boolean;
  access?: PhasesRecoveryAccess;
  operation: PhasesRecoveryOperation | null;
  claims: PhasesRecoveryClaim[];
}

const nonempty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
const count = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const optionalText = (value: unknown): boolean => value === undefined || value === null || typeof value === 'string';
function validAccess(value: PhasesRecoveryAccess | undefined): boolean {
  return value === undefined || !!(value && typeof value.authenticated === 'boolean' &&
    typeof value.owner_authorized === 'boolean' && nonempty(value.session_scope) &&
    ['OWNER', 'AUTHENTICATED_NON_OWNER', 'UNAUTHENTICATED'].includes(value.session_class) &&
    value.capability === PHASES_RECOVERY_CAPABILITY && (!value.owner_authorized ||
      (value.authenticated && value.session_class === 'OWNER' && ['FULL', 'TRADING'].includes(value.session_scope))));
}
export function phasesRecoveryOwnerAuthorized(status: PhasesRecoveryStatus): boolean {
  return status.access?.authenticated === true && status.access.owner_authorized === true &&
    status.access.session_class === 'OWNER' && status.access.capability === PHASES_RECOVERY_CAPABILITY &&
    ['FULL', 'TRADING'].includes(status.access.session_scope);
}

export class PhasesRecoveryResponseError extends Error {
  constructor() { super('Phases recovery response is invalid'); this.name = 'PhasesRecoveryResponseError'; }
}
export class PhasesRecoveryTimeoutError extends Error {
  constructor() { super('Phases recovery status request timed out'); this.name = 'PhasesRecoveryTimeoutError'; }
}
function validClaims(value: unknown): value is PhasesRecoveryClaim[] {
  if (!Array.isArray(value) || value.length > 4) return false;
  return value.every(row => row && ['contract_id', 'claim_identity', 'request_id', 'callback_identity', 'label', 'state']
    .every(key => nonempty(row[key])) && count(row.source_requests) && count(row.rows_added) &&
    optionalText(row.route_state) && optionalText(row.reason) && optionalText(row.counts_evidence) &&
    optionalText(row.original_source_outcome) && optionalText(row.continuation_response_state) &&
    (row.persistence_counts === undefined || row.persistence_counts === null ||
      (typeof row.persistence_counts === 'object' && Object.values(row.persistence_counts).every(count))) &&
    (row.persistence_complete === undefined || row.persistence_complete === null || typeof row.persistence_complete === 'boolean') && optionalText(row.persistence_intent_state) &&
    (row.response_references === undefined || (Array.isArray(row.response_references) && row.response_references.every((response: { http_status?: unknown; error?: unknown; url?: unknown }) =>
      response && optionalText(response.url) && optionalText(response.error) && (response.http_status === null || response.http_status === undefined || count(response.http_status)))))) &&
    new Set(value.map(row => row.contract_id)).size === value.length &&
    new Set(value.map(row => row.claim_identity)).size === value.length;
}

export function decodePhasesRecoveryStatus(raw: unknown): PhasesRecoveryStatus {
  const p = raw as PhasesRecoveryStatus;
  if (!p || p.success !== true || p.schema !== 'mack_phases_history_recovery_v1' ||
      p.scope !== PHASES_RECOVERY_SCOPE || !nonempty(p.authority_state) ||
      typeof p.resident_available !== 'boolean' || typeof p.can_submit !== 'boolean' || !validClaims(p.claims) || !validAccess(p.access) ||
      (p.operation !== null && (!p.operation || !nonempty(p.operation.request_id) || !nonempty(p.operation.state) ||
        !nonempty(p.operation.requested_at) || !optionalText(p.operation.started_at) ||
        !optionalText(p.operation.ended_at) || !validClaims(p.operation.claims))) ||
      (p.can_submit && (p.authority_state !== 'VERIFIED' || !p.resident_available ||
        p.operation !== null || p.claims.length !== 4 || !phasesRecoveryOwnerAuthorized(p)))) {
    throw new PhasesRecoveryResponseError();
  }
  return p;
}

export async function readPhasesRecovery(token?: string | null, signal?: AbortSignal): Promise<PhasesRecoveryStatus> {
  const response = await axios.get(`${apiBase()}/mack/phases/recovery`, {
    headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' }, signal,
    timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  return decodePhasesRecoveryStatus(response.data);
}

// The server resolves all claim identities and source routes from durable state.
export async function submitPhasesRecovery(token?: string | null, signal?: AbortSignal): Promise<PhasesRecoveryStatus> {
  const response = await axios.post(`${apiBase()}/mack/phases/recovery`, { scope: PHASES_RECOVERY_SCOPE }, {
    headers: { Authorization: `Bearer ${token || authToken()}` }, signal,
    timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  return decodePhasesRecoveryStatus(response.data);
}

export function phasesRecoveryFailure(error: unknown, submission = false): string {
  if (error instanceof PhasesRecoveryTimeoutError || (axios.isAxiosError(error) && ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code || ''))) {
    return submission ? 'The recovery acknowledgement timed out. The server operation was not cancelled. Refresh status before submitting again.'
      : 'The recovery status read timed out. The server operation was not cancelled. Refresh status to read the existing receipt.';
  }
  if (error instanceof PhasesRecoveryResponseError) return submission
    ? 'The backend returned an invalid recovery acknowledgement. Refresh status before submitting again.'
    : 'The backend returned an invalid recovery status. Refresh status; continuation remains disabled.';
  const status = axios.isAxiosError(error) ? error.response?.status : null;
  const code = axios.isAxiosError(error) ? error.response?.data?.error_code : null;
  if (status === 401) return 'Your session is unauthenticated or has expired. Sign in again to view recovery.';
  if (status === 403 && code === 'PHASES_RECOVERY_OWNER_PERMISSION_REQUIRED') {
    return 'This authenticated session does not have owner permission for Phases history recovery.';
  }
  if (status === 403 && code === 'PHASES_RECOVERY_TRADING_OWNER_REQUIRED') {
    return 'The backend owner-access repair is awaiting activation. Refresh status after activation.';
  }
  if (status === 403) return 'This session cannot authorise Phases history recovery.';
  if (status === 404) return 'History recovery is awaiting backend activation.';
  if (status === 409) return 'Recovery is withheld. Refresh status to inspect the current authority and claim states.';
  if (status === 503) return 'Resident recovery is unavailable. Refresh status after activation.';
  if (status === 400) return 'The recovery request was rejected. Refresh status before submitting again.';
  if (axios.isAxiosError(error) && !error.response) return submission
    ? 'The connection failed before a recovery acknowledgement was received. Refresh status before submitting again.'
    : 'No backend response was received. Refresh status to check the connection.';
  return submission
    ? 'The recovery acknowledgement could not be confirmed. Refresh status before submitting again.'
    : 'History recovery status is unavailable. Refresh status to check again.';
}
